import React from 'react';
import { LaufzeitAbschnitt, PhaseArt, PHASE_NAME, zeitraumAnzeige } from './projektStand';

/**
 * Der Doppelring des Projektstands: aussen die ZEIT, innen die LEISTUNG —
 * beide auf derselben Uhr, beginnend oben, im Uhrzeigersinn. Wie weit der
 * innere Ring hinter dem aeusseren zurueckbleibt, sieht man, ohne eine Zahl
 * zu lesen.
 *
 * In der Gesamtansicht ist der Zeitring in die Phasen geteilt (Planung
 * heller, Ausfuehrung kraeftiger, eine Luecke dazwischen); ein Klick auf
 * einen Abschnitt schaltet in die Phase.
 *
 * Die Werte kommen bereits animiert herein (useZaehler in der Karte) — der
 * Ring waechst im Gleichschritt mit der Zahl in der Mitte.
 */

const GROESSE = 144;
const MITTE = GROESSE / 2;
const R_AUSSEN = 63;
const R_INNEN = 49;
const STRICH = 10;
const LUECKE_PX = 5;

const ZEIT = '#2f83f6';          // --color-blue-500
const ZEIT_PLANUNG = '#93c5fd';  // --color-blue-300
const ZEIT_WARNUNG = '#dd2b2b';  // --color-red-500
const ZEIT_RUHT = '#6a7686';     // --color-slate-400
const SPUR = '#f1f5f9';          // --color-slate-100
const SPUR_ZEIT = '#dbeafe';     // --color-blue-100

interface Props {
    /** Zeitanteil 0–100, schon animiert; null = kein Zeitraum. */
    zeit: number | null;
    /** Leistungsanteil 0–∞, schon animiert; null = kein Soll. */
    leistung: number | null;
    /** Farbe der Leistung — dieselbe Regel wie der Balken (gruen / orange / rot). */
    leistungFarbe: string;
    /** Phasen fuer den geteilten Zeitring; leer = ein Ring. */
    abschnitte: LaufzeitAbschnitt[];
    zeitZustand: 'normal' | 'warnung' | 'ruht';
    /** In einer Phasenansicht: die Phase, deren Farbe der ungeteilte Zeitring traegt. */
    phase?: PhaseArt;
    onPhase?: (art: PhaseArt) => void;
    children?: React.ReactNode;
}

/** Ein Bogen als Kreis mit Strichmuster: Start und Laenge in Prozent des Umfangs. */
const Bogen: React.FC<{
    r: number; start: number; laenge: number; farbe: string; onClick?: () => void; titel?: string;
}> = ({ r, start, laenge, farbe, onClick, titel }) => {
    const umfang = 2 * Math.PI * r;
    const l = Math.max(0, Math.min(100, laenge)) / 100 * umfang;
    if (l < 0.5) return null;
    return (
        <circle
            cx={MITTE} cy={MITTE} r={r}
            fill="none" stroke={farbe} strokeWidth={STRICH} strokeLinecap="round"
            strokeDasharray={`${l} ${umfang}`}
            strokeDashoffset={-(start / 100) * umfang}
            transform={`rotate(-90 ${MITTE} ${MITTE})`}
            onClick={onClick}
            className={onClick ? 'cursor-pointer' : undefined}
        >
            {titel && <title>{titel}</title>}
        </circle>
    );
};

export const StandRinge: React.FC<Props> = ({ zeit, leistung, leistungFarbe, abschnitte, zeitZustand, phase, onPhase, children }) => {
    const umfangAussen = 2 * Math.PI * R_AUSSEN;
    // Luecke zwischen den Phasen in Prozent des Umfangs, plus die Rundung der Kappen.
    const luecke = ((LUECKE_PX + STRICH) / umfangAussen) * 100;
    const zeitFarbe = (art?: PhaseArt) => zeitZustand === 'warnung' ? ZEIT_WARNUNG
        : zeitZustand === 'ruht' ? ZEIT_RUHT
        : art === 'planung' ? ZEIT_PLANUNG : ZEIT;

    return (
        <div className="relative shrink-0" style={{ width: GROESSE, height: GROESSE }}>
            <svg width={GROESSE} height={GROESSE} className="block overflow-visible" aria-hidden="true">
                {/* Spuren */}
                <circle cx={MITTE} cy={MITTE} r={R_INNEN} fill="none" stroke={SPUR} strokeWidth={STRICH} />
                {abschnitte.length === 0 && <circle cx={MITTE} cy={MITTE} r={R_AUSSEN} fill="none" stroke={SPUR_ZEIT} strokeWidth={STRICH} />}

                {/* Zeit — geteilt nach Phasen oder ein Ring */}
                {abschnitte.length > 0 ? abschnitte.map(a => {
                    const start = a.von + luecke / 2;
                    const laenge = Math.max(0, a.bis - a.von - luecke);
                    const gefuellt = zeit === null ? 0 : Math.max(0, Math.min(laenge, zeit - start));
                    const titel = `${PHASE_NAME[a.art]}: ${zeitraumAnzeige(a.vonTag, a.bisTag)} — anklicken für die Phase`;
                    const klick = onPhase ? () => onPhase(a.art) : undefined;
                    return (
                        <g key={a.art}>
                            <Bogen r={R_AUSSEN} start={start} laenge={laenge} farbe={SPUR_ZEIT} onClick={klick} titel={titel} />
                            <Bogen r={R_AUSSEN} start={start} laenge={gefuellt} farbe={zeitFarbe(a.art)} onClick={klick} titel={titel} />
                        </g>
                    );
                }) : (
                    zeit !== null && <Bogen r={R_AUSSEN} start={0} laenge={zeit} farbe={zeitFarbe(phase)} />
                )}

                {/* Leistung — ueber 100 % laeuft der Ring nicht weiter, die Farbe sagt es */}
                {leistung !== null && <Bogen r={R_INNEN} start={0} laenge={Math.min(100, leistung)} farbe={leistungFarbe} />}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                {children}
            </div>
        </div>
    );
};
